import { getLinkExplorerAddress } from "@/components/dashboard/commons"
import { NFTDetailType } from "@/hooks/useFetchNFT"
import { Chip, Link } from "@mui/material"
import FormControlLabel, { FormControlLabelProps } from "@mui/material/FormControlLabel"
import { useRadioGroup } from "@mui/material/RadioGroup"
import { styled } from "@mui/material/styles"
import { Dispatch, useCallback, useEffect, useRef, useState } from "react"

import LinkIcon from "@/assets/icons/link.svg"
import DefaultNFTEmpty from "@/assets/images/default-nft-empty.png"
import Box from "@mui/material/Box"
import Radio from "@mui/material/Radio"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Image from "next/image"

import { networkAtom } from "@/atoms/settings"
import { SelectedReserveType } from "@/atoms/shared"
import Button from "@/components/base/Button"
import useTrans from "@/hooks/useTrans"
import { useAtomValue } from "jotai"
import { SetStateAction } from "jotai/vanilla"
import * as styles from "../styles"
import NFTDetailDialog from "./NFTDetailDialog"

interface StyledFormControlLabelProps extends FormControlLabelProps {
  checked: boolean
}

const StyledNFTItemLabel = styled((props: StyledFormControlLabelProps) => <FormControlLabel {...props} />)(
  ({ checked }) => ({
    position: "absolute",
    top: -6,
    right: -20,
    ".MuiButtonBase-root": {
      display: checked ? "block" : "none",
      "&.Mui-checked": {
        color: "#8AA683",
      },
    },
    ".MuiFormControlLabel-label": {
      display: "none",
    },
  }),
)

export type NFTItemProps = {
  nftDetail: NFTDetailType
  shouldRefreshCheckbox?: boolean
  selectedReserve?: SelectedReserveType
  nftMintSelected?: string
  setNftMintSelected: Dispatch<SetStateAction<string | undefined>>
  handleClaimClick?: (mint: string) => void
  showSelected?: boolean
  showMintAddress?: boolean
  isClaimingVoucher?: boolean
}

const NFTItem = ({
  nftDetail,
  shouldRefreshCheckbox,
  selectedReserve,
  nftMintSelected,
  setNftMintSelected,
  handleClaimClick,
  showSelected = true,
  isClaimingVoucher,
}: NFTItemProps) => {
  const { name, mintAddress, imageUrl, displayMintAddress, detail } = nftDetail
  const trans = useTrans()
  const radioGroup = useRadioGroup()
  const refCheckbox = useRef<HTMLButtonElement>()
  const network = useAtomValue(networkAtom)
  const [isOpenDetail, setIsOpenDetail] = useState(false)

  const isValidStartDate = Boolean(isClaimingVoucher || detail?.isValidStartDate)

  const isValid =
    !!detail &&
    isValidStartDate &&
    detail.isValidEndDate &&
    (detail.eligibleTokens === "" ||
      !selectedReserve ||
      (selectedReserve && detail.eligibleTokens.split(",").includes(selectedReserve.mintAddress)))
  let checked = false

  if (nftMintSelected) {
    checked = showSelected && nftMintSelected === mintAddress
  }

  const handleOpenNFTDetailModal = useCallback(() => {
    setIsOpenDetail(true)
  }, [])
  const handleCloseNFTDetailModal = useCallback(() => {
    setIsOpenDetail(false)
  }, [])

  const handleNFTItemClick = useCallback(() => {
    if (!radioGroup || !refCheckbox.current) return

    refCheckbox.current.click()
  }, [radioGroup])

  useEffect(() => {
    setNftMintSelected(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefreshCheckbox])

  return (
    <>
      <Box sx={styles.styleGradient(checked)}>
        <Stack data-testid={`nft-item-${mintAddress}`} onClick={handleNFTItemClick} sx={styles.boxNftItem(isValid)}>
          {detail && <Chip sx={styles.boxNftItemDiscount} label={`${detail.discountPercentage}%`} />}
          <StyledNFTItemLabel
            disabled={!isValid}
            label=''
            checked={checked}
            value={mintAddress}
            control={<Radio inputRef={refCheckbox} />}
          />
          <Box sx={styles.boxNftItemImage}>
            <Image
              src={imageUrl ?? DefaultNFTEmpty}
              width={220}
              height={220}
              alt='nft-image'
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
          <Stack className='bg-secondary py-3 px-[0.625rem] rounded-br-lg rounded-bl-lg' gap={1.5}>
            <Stack gap={0.5}>
              <Typography
                sx={styles.boxNftItemDetailTitle}
                className='max-w-full w-fit border-b border-dashed border-neutral-600'
                onClick={handleOpenNFTDetailModal}
              >
                {name}
              </Typography>
              <Link
                href={getLinkExplorerAddress(network, mintAddress)}
                target='_blank'
                sx={styles.boxNftItemDetailSubTitle}
              >
                <Image src={LinkIcon} width={12} height={12} alt='nft-image' />
                <span>{displayMintAddress}</span>
              </Link>
            </Stack>
            {handleClaimClick && (
              <Button
                variant='primary'
                fullWidth
                size='sm'
                onClick={() => handleClaimClick(mintAddress)}
                data-testid='button-nft-item-claim'
              >
                {trans.dashboard.hero.nft_claim}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {isOpenDetail && (
        <NFTDetailDialog isOpen={isOpenDetail} handleCloseModal={handleCloseNFTDetailModal} selectedNFT={nftDetail} />
      )}
    </>
  )
}

export default NFTItem
